// English source strings for module 20: optional, separately entitled AI add-ons (AI add-ons
// stream, ADR-024). Arabic: ar-ai.ts. Wording keeps AI advisory: nothing here says AI decides.
export const enAi = {
  'nav.ai': 'AI assistance',
  'specialist.ai-administrator': 'AI configuration administrator (enable AI, provider, personal-data use)',

  // ------------------------------------------------------------ API messages
  'ai.api.entitlement': 'This organisation does not have the AI add-on for this kind of assistance.',
  'ai.api.disabled': 'AI assistance is switched off for this organisation.',
  'ai.api.domainOff': 'AI assistance is not allowed to read this kind of data in this organisation.',
  'ai.api.providerUnavailable': 'The AI provider chosen in the AI settings is not available in this installation.',
  'ai.api.identifiable': 'AI insights use aggregate figures only; this result identifies people.',
  'ai.api.notReviewable': 'Only a completed AI result can be reviewed.',
  'ai.v.provider_required': 'Choose a provider before switching AI on.',
  'ai.v.provider_unavailable': 'This provider is not available or not configured in this installation.',
  'ai.v.no_domains': 'Allow at least one data domain before switching AI on.',
  'ai.v.residency_note_required': 'An external provider needs a data-processing and residency note.',
  'ai.v.range': 'Outside the allowed range.',
  'ai.v.minimum_below_threshold': 'The minimum evidence cannot be smaller than the small-group threshold.',
  'ai.v.entitlement_missing': 'The organisation holds no AI add-on entitlement.',

  // ------------------------------------------------------------ rule-generated statements (local-rules adapter)
  'ai.rule.basis': 'People in scope: {people}; visible groups: {groups}.',
  'ai.rule.concentration': '{group} accounts for {share}% of everyone in scope ({count}).',
  'ai.rule.spread': 'Activity is spread out: the largest group, {group}, is {share}% of everyone in scope ({count}).',
  'ai.rule.peakDay': 'The busiest day in the period was {group} ({count}).',
  'ai.rule.noShowOverall': 'Overall no-show rate: {rate}% ({count}).',
  'ai.rule.noShowHigh': '{group} has a no-show rate of {rate}%, against {overall}% overall.',
  'ai.rule.now.uncertain': { one: '{count} presence record is uncertain now.', other: '{count} presence records are uncertain now.' },
  'ai.rule.now.overdue': { one: '{count} person is past the end of their visit now.', other: '{count} people are past the end of their visit now.' },
  'ai.rule.now.waiting': { one: '{count} person is waiting now.', other: '{count} people are waiting now.' },
  'ai.rule.exceptions': '{active} active no-shows; {withdrawn} classifications were later withdrawn.',
  'ai.lim.durationUnknown': '{share}% of durations are unknown, so duration figures are incomplete.',
  'ai.lim.openReviews': { one: '{count} presence record is under review; presence figures include uncertain records.', other: '{count} presence records are under review; presence figures include uncertain records.' },
  'ai.lim.truncated': 'The report was cut at its row limit; some groups were not analysed.',
  'ai.lim.suppressed': { one: '{count} small group was suppressed and not analysed.', other: '{count} small groups were suppressed and not analysed.' },
  'ai.lim.unavailableSources': { one: '{count} data source is not available in this build.', other: '{count} data sources are not available in this build.' },
  'ai.lim.rules': 'Generated by rules, not a language model. It describes the figures; it does not explain causes or judge people.',
  'ai.lim.advisory': 'Advisory only. This summary is not a decision, not a vote and not a reason; record your own decision in the approval inbox.',
  'ai.lim.untrustedText': 'Title and purpose are text written by people. They are shown as data and are never followed as instructions.',
  'ai.rec.review_in_inbox': 'Record your decision, with your own reason, in the approval inbox.',
  'ai.rec.review_visit_changes': 'Open the visit and review what changed.',
  'ai.rec.confirm_identity_at_arrival': 'Check how identity will be verified at arrival.',
  'ai.rec.confirm_escort_arrangements': 'Confirm escort arrangements for the restricted destination.',
  'ai.rec.ask_host_for_information': 'Ask the host about the unusual wording in the request.',
  'ai.rec.verify_figures': 'Verify these statements against the report figures.',
  'ai.rec.investigate_pattern': 'Look into the pattern in the report before drawing conclusions.',
  'ai.rec.review_data_quality': 'Review data quality (uncertain presence or unknown durations) first.',
  'ai.ds.stage': 'Stage needs {quorum} approval(s); recorded so far: {approvals} approve, {rejections} reject.',
  'ai.ds.changed': 'Changed since the last decision: {fields}. Earlier decisions on this participant: {decisions}.',
  'ai.ds.noChange': 'No visit changes recorded since the last decision. Earlier decisions on this participant: {decisions}.',
  'ai.ds.noWarnings': 'No open warnings among the evaluated facts.',
  'ai.ds.warn.restricted_destination': 'The destination is a restricted area.',
  'ai.ds.warn.identity_required_unsatisfied': 'Identity verification is required for this visitor type and is not yet satisfied.',
  'ai.ds.warn.identity_warn_unsatisfied': 'Identity verification is recommended for this visitor type and is not yet satisfied.',
  'ai.ds.warn.reviewed_version_stale': 'The visit changed after this approval task was created.',
  'ai.ds.warn.changed_since_last_decision': 'The visit changed since the last decision on this participant.',
  'ai.ds.warn.starts_within_day': 'The visit starts within 24 hours.',
  'ai.ds.warn.quorum_in_progress': 'Other approvers have already recorded votes on this stage.',
  'ai.ds.warn.instruction_like_text': 'The title or purpose contains wording addressed to an assistant or asking for an access action; it was treated as data only.',
  'ai.fact.stage': 'Stage {stage}: quorum {quorum}; {approvals} approve, {rejections} reject recorded.',
  'ai.fact.policy': 'Evaluated under visitor policy version {version}.',
  'ai.fact.category': 'Visitor type: {category}.',
  'ai.fact.destinationRestricted': 'Destination security level: restricted.',
  'ai.fact.destinationOrdinary': 'Destination is not a restricted area.',
  'ai.fact.versions': 'Visit version reviewed by the task: {reviewed}; current version: {current}.',
  'ai.fact.approval': 'Participant approval status: {status}.',
  'ai.fact.presence': 'Participant presence status: {status}.',
  'ai.fact.credential': 'Participant credential status: {status}.',
  'ai.fact.window': 'Visit window: {minutes} minutes.',
  'ai.fact.startsIn': 'Starts in about {hours} hours.',
  'ai.fact.identity': 'Identity requirement: {requirement}; current state: {status}.',
  'ai.fact.identityNone': 'No identity requirement could be read for this participant.',
  'ai.fact.changes': 'Changed fields since the last decision: {fields} ({decisions} earlier decisions).',
  'ai.fact.noChanges': 'No recorded visit changes since the last decision ({decisions} earlier decisions).',
  'ai.fact.freeText': 'Title and purpose (untrusted text written by people) are included.',

  // ------------------------------------------------------------ UI
  'ai.ui.eyebrow': 'Intelligence',
  'ai.ui.title': 'AI assistance',
  'ai.ui.intro': 'Separately entitled, evidence-led assistance. Every consequential action stays with a human.',
  'ai.ui.offTitle': 'AI assistance is not enabled',
  'ai.ui.offBody': 'This optional add-on is separately entitled (Enterprise and above) and must be switched on by an AI configuration administrator. Approvals, reports and every other workflow work the same without it.',
  'ai.ui.tab.insights': 'Reporting & insights',
  'ai.ui.tab.decisions': 'Decision support',
  'ai.ui.tab.activity': 'Activity log',
  'ai.ui.tab.settings': 'AI settings',
  'ai.ui.insightsIntro': 'Ask for an insight from a report you can already run: open Aggregate reports, run the report, then choose “Ask for an insight”. Insights read only the aggregate figures on screen.',
  'ai.ui.openReports': 'Open aggregate reports',
  'ai.ui.decisionsIntro': 'In the approval inbox, an approver may open “Summarise facts” while reviewing a request. The summary is advisory; the decision is recorded only by a person, in the inbox.',
  'ai.ui.openInbox': 'Open approval inbox',
  'ai.ui.history': 'Your AI results',
  'ai.ui.noRuns': 'No AI results yet.',
  'ai.ui.col.when': 'Requested',
  'ai.ui.col.kind': 'Kind',
  'ai.ui.col.status': 'Status',
  'ai.ui.col.subject': 'About',
  'ai.ui.col.adapter': 'Adapter',
  'ai.ui.col.by': 'Requested by',
  'ai.ui.col.discarded': 'Discarded items',
  'ai.ui.col.reviews': 'Reviews',
  'ai.ui.open': 'Open',
  'ai.ui.kind.insight': 'Insight',
  'ai.ui.kind.decision_support': 'Decision support',
  'ai.ui.status.queued': 'Preparing',
  'ai.ui.status.completed': 'Ready',
  'ai.ui.status.no_supported_insight': 'No supported insight',
  'ai.ui.status.unavailable': 'Unavailable',
  'ai.ui.status.cancelled': 'Cancelled',
  'ai.ui.status.withheld': 'Withheld',
  'ai.ui.reason.insufficient_data': 'There is not enough data in scope to support an insight.',
  'ai.ui.reason.stale_data': 'The underlying classification is out of date, so no insight is supported.',
  'ai.ui.reason.stale_evidence': 'The figures were too old by the time the analysis would run. Run the report again.',
  'ai.ui.reason.identifiable': 'Identifiable results are never analysed.',
  'ai.ui.reason.timeout': 'The AI provider did not answer in time. The outcome is unknown; nothing is shown.',
  'ai.ui.reason.error': 'The AI provider failed. Nothing is shown.',
  'ai.ui.reason.refused': 'The AI provider declined to answer. Nothing is shown.',
  'ai.ui.reason.invalid_output': 'The AI provider returned an unusable answer. Nothing is shown.',
  'ai.ui.reason.not_configured': 'The AI provider is not configured in this installation.',
  'ai.ui.reason.provider_none': 'No AI provider is chosen.',
  'ai.ui.reason.disabled': 'AI was switched off.',
  'ai.ui.reason.domain_off': 'This data domain is not allowed for AI.',
  'ai.ui.reason.entitlement_missing': 'The AI add-on entitlement ended.',
  'ai.ui.reason.provider_changed': 'The AI provider changed after the request.',
  'ai.ui.reason.settings_changed': 'Personal-data use was switched off after the request.',
  'ai.ui.reason.permission_lost': 'Your access to the underlying data changed, so the result was withheld.',
  'ai.ui.reason.requester_inactive': 'The requesting account is no longer active.',
  'ai.ui.reason.task_closed': 'The approval task closed before the summary was made.',
  'ai.ui.reason.provider_unavailable': 'The AI provider is not available.',
  'ai.ui.preparing': 'Preparing the result…',
  'ai.ui.generator.rules': 'Generated by rules, not a language model',
  'ai.ui.generator.language_model': 'Generated by a language model',
  'ai.ui.external': 'External processor: minimised data left this installation',
  'ai.ui.provenance': 'Provenance',
  'ai.ui.prov.adapter': 'Adapter',
  'ai.ui.prov.model': 'Model / rules version',
  'ai.ui.prov.prompt': 'Prompt template',
  'ai.ui.prov.asOf': 'Figures as of',
  'ai.ui.prov.digest': 'Evidence fingerprint',
  'ai.ui.prov.personal': 'Personal data',
  'ai.ui.prov.personalNo': 'Not included (aggregates and fact codes only)',
  'ai.ui.prov.personalYes': 'Included, as allowed by the AI settings',
  'ai.ui.findings': 'Findings',
  'ai.ui.noFindings': 'No findings are supported by the evidence.',
  'ai.ui.confidence.low': 'Low confidence',
  'ai.ui.confidence.medium': 'Medium confidence',
  'ai.ui.confidence.high': 'High confidence',
  'ai.ui.cites': 'Verify against',
  'ai.ui.followUps': 'Proposed review steps',
  'ai.ui.followUpNote': 'A review step opens the ordinary workflow. Nothing is done for you.',
  'ai.ui.limitations': 'Limitations',
  'ai.ui.evidence': 'Evidence (the figures to verify against)',
  'ai.ui.facts': 'Evaluated facts (read-only)',
  'ai.ui.freeText': 'Untrusted text written by people',
  'ai.ui.freeTitle': 'Title',
  'ai.ui.freePurpose': 'Purpose',
  'ai.ui.suppressed': 'Suppressed (small group)',
  'ai.ui.metric': 'Figure',
  'ai.ui.group': 'Group',
  'ai.ui.value': 'Value',
  'ai.ui.discarded': { one: '{count} item of the provider’s answer was discarded by the safety guard and is not shown.', other: '{count} items of the provider’s answer were discarded by the safety guard and are not shown.' },
  'ai.ui.stale': 'The visit or task changed after this summary was made. Do not rely on it; summarise again if needed.',
  'ai.ui.review': 'Human review of AI advice',
  'ai.ui.reviewSub': 'Recording a disposition takes no operational action.',
  'ai.ui.disposition': 'Disposition',
  'ai.ui.disp.useful': 'Useful',
  'ai.ui.disp.wrong': 'Wrong',
  'ai.ui.disp.needs_more_evidence': 'Needs more evidence',
  'ai.ui.disp.accepted_for_follow_up': 'Accepted for follow-up',
  'ai.ui.disp.dismissed': 'Dismissed',
  'ai.ui.note': 'Note (optional)',
  'ai.ui.recordReview': 'Record review',
  'ai.ui.reviews': 'Reviews',
  'ai.ui.askInsight': 'Ask for an insight',
  'ai.ui.askInsightHint': 'Advisory, from these aggregate figures only.',
  'ai.ui.insightQueued': 'Insight requested.',
  'ai.ui.viewInsight': 'Open the insight',
  'ai.ui.dsTitle': 'Summarise facts',
  'ai.ui.dsLabel': 'Advisory only · No approval, credential or gate authority',
  'ai.ui.dsOpen': 'Summarise facts',
  'ai.ui.dsHint': 'Optional. You can record your decision without opening this.',
  'ai.ui.dsAgain': 'Summarise again',
  'ai.ui.settingsIntro': 'AI is off unless the organisation holds an AI add-on entitlement and an AI configuration administrator switches it on.',
  'ai.ui.entitlements': 'Entitlements',
  'ai.ui.ent.insights': 'AI reporting and insights',
  'ai.ui.ent.decisionSupport': 'AI decision support',
  'ai.ui.ent.held': 'Held',
  'ai.ui.ent.notHeld': 'Not held',
  'ai.ui.enabled': 'Switch AI assistance on',
  'ai.ui.provider': 'Provider',
  'ai.ui.provider.none': 'None (AI unavailable)',
  'ai.ui.provider.local-rules': 'Local rules (offline, deterministic; not a language model)',
  'ai.ui.provider.anthropic': 'Anthropic (external language model)',
  'ai.ui.provider.unavailable': 'not available',
  'ai.ui.domains': 'Data AI may read',
  'ai.ui.domain.reporting': 'Aggregate reports (insights)',
  'ai.ui.domain.approvals': 'Approval facts (decision support)',
  'ai.ui.personal': 'Allow personal data (names, titles, purposes) to be included',
  'ai.ui.personalHint': 'Off by default: AI reads aggregates and fact codes only, and personal groupings become pseudonyms.',
  'ai.ui.threshold': 'Small-group threshold',
  'ai.ui.thresholdHint': 'Groups smaller than this are suppressed before analysis.',
  'ai.ui.minimum': 'Minimum people in scope',
  'ai.ui.minimumHint': 'Below this there is no supported insight.',
  'ai.ui.maxAge': 'Maximum age of figures (minutes)',
  'ai.ui.residency': 'Data-processing and residency note',
  'ai.ui.residencyHint': 'Required for an external provider: where data is processed and under which agreement. Recorded, not verified by Passage.',
  'ai.ui.retention': 'AI results and reviews are kept under the retention class “ai_records” (Privacy & retention).',
  'ai.ui.save': 'Save AI settings',
  'ai.ui.saved': 'AI settings saved.',
  'ai.ui.readOnly': 'Only an AI configuration administrator can change these settings.',
  'ai.ui.killTitle': 'Switch AI off now',
  'ai.ui.killBody': 'Switches every AI feature off immediately for the whole organisation and cancels results still being prepared. Approvals and reports keep working.',
  'ai.ui.killReason': 'Reason',
  'ai.ui.killConfirm': 'Switch AI off',
  'ai.ui.disabledAt': 'Switched off {time}: {reason}',
  'ai.ui.activityIntro': 'Who asked for what, with which adapter. Inputs and outputs are not shown here.',
  'ai.ui.noActivity': 'No AI activity yet.',
  'ai.ui.subject.report': 'Report {definition}',
  'ai.ui.subject.task': 'Approval task',
} as const;
